import { useMaintenanceEvents, useDeleteMaintenanceEvent } from '../../hooks/useMaintenance'
import { usePrinters } from '../../hooks/usePrinters'
import { formatDate, formatMoney, monthKey } from '../../lib/format'
import { Skeleton } from '../../components/ui'

export default function MaintenanceHistory() {
  const { data: events } = useMaintenanceEvents()
  const { data: printers } = usePrinters()
  const deleteEvent = useDeleteMaintenanceEvent()

  if (!events || !printers) return (
    <div>
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} height={60} style={{ marginBottom: 8 }} />
      ))}
    </div>
  )

  const printerById = new Map(printers.map((p) => [p.id, p.name]))
  const groups: Record<string, typeof events> = {}
  for (const e of events) {
    const key = monthKey(e.event_date)
    if (!groups[key]) groups[key] = []
    groups[key].push(e)
  }

  if (events.length === 0) {
    return (
      <div style={{
        padding: 'var(--space-6)',
        textAlign: 'center',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        color: 'var(--color-text-muted)',
      }}>
        No maintenance logged yet.
      </div>
    )
  }

  return (
    <div
      style={{
        maxHeight: '70vh',
        overflowY: 'auto',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-3)',
        background: 'var(--color-bg)',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {Object.entries(groups)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([month, list]) => (
          <div key={month} style={{ marginBottom: 'var(--space-6)' }}>
            <div style={{
              background: 'var(--color-surface-2)',
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              marginBottom: 'var(--space-2)',
            }}>
              {monthLabel(month)}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {list.map((e) => (
                <li key={e.id} style={{
                  background: 'var(--color-surface)',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-2)',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                      {formatTask(e.task_type)} · {printerById.get(e.printer_id) ?? '?'}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                      {formatDate(e.event_date)}
                      {e.printer_hours_at_event != null && ` · @${e.printer_hours_at_event}h`}
                      {e.cost != null && ` · ${formatMoney(Number(e.cost))}`}
                    </div>
                    {e.notes && (
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
                        {e.notes}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this maintenance log?')) deleteEvent.mutate(e.id)
                    }}
                    style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  )
}

function formatTask(t: string): string {
  return t.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-')
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}
